import { useCallback } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import DashboardService from '@/services/DashboardService';
import type { DashboardViewOpts } from '@/services/DashboardService';
import { engageRosterScope } from '@/config/program';
import { DASHBOARD_KEYS } from './keys';

export function useDashboardView(role: string | null | undefined, opts: DashboardViewOpts = {}) {
  return useQuery({
    queryKey: DASHBOARD_KEYS.view(role ?? '', opts),
    queryFn: () => DashboardService.getDashboardView(role!, opts),
    enabled: !!role,
    // Switching role changes the key. Without this the board has no data for the
    // new key, so useAppData reports `loading` and RootLayout swaps the whole
    // route for the boot spinner — tearing the dashboard down mid-session and
    // dropping the scroll position (and the roster's local sort/filter state).
    // Keep the previous role's board on screen until the new one lands; the
    // first load has no previous data, so the boot spinner still shows.
    placeholderData: keepPreviousData,
  });
}

// Imperative one-shot for pagination / debounce-filter / cancellable polling flows.
// `fresh` bypasses the cache: a polling tick would otherwise re-read data up to 5 min old.
export function useFetchDashboardView() {
  const qc = useQueryClient();
  return useCallback(
    (role: string, opts: DashboardViewOpts = {}, { fresh = false }: { fresh?: boolean } = {}) => qc.fetchQuery({
      queryKey: DASHBOARD_KEYS.view(role, opts),
      queryFn: () => DashboardService.getDashboardView(role, opts),
      ...(fresh ? { staleTime: 0 } : {}),
    }),
    [qc],
  );
}

// The sidebar's Engage badge: unread conversations in the roster Engage lists, whatever the viewer's
// role. Polling is the caller's idle-gated tick; a failed refetch keeps the last count.
export function useUnreadConversationCount() {
  const scope = engageRosterScope();
  return useQuery({
    queryKey: DASHBOARD_KEYS.unreadCount(scope.programid),
    queryFn: () => DashboardService.getUnreadConversationCount(scope),
    staleTime: 60_000,
    // A hidden tab skips its refresh ticks, so a returning user gets a fresh count.
    refetchOnWindowFocus: true,
  });
}

// Refreshes the badge without subscribing to it — for Engage, so its list and the badge move together.
// Joins a fetch already in flight instead of cancelling it.
export function useRefreshUnreadCount() {
  const qc = useQueryClient();
  return useCallback(
    () => qc.invalidateQueries(
      { queryKey: DASHBOARD_KEYS.unreadCount(engageRosterScope().programid) },
      { cancelRefetch: false },
    ),
    [qc],
  );
}

// Moves the badge with Engage's optimistic dot; the read/unread mutation's delayed invalidate reconciles it.
export function useAdjustUnreadCount() {
  const qc = useQueryClient();
  return useCallback(
    (delta: number) => qc.setQueryData<number>(
      DASHBOARD_KEYS.unreadCount(engageRosterScope().programid),
      (n) => (n === undefined ? n : Math.max(0, n + delta)),
    ),
    [qc],
  );
}

export type { DashboardViewOpts };
